import { axios } from '@boot/axios.js';
import { i18n } from '@boot/i18n.js';
import { notifyError } from '@boot/popups.js';

// Timeout for fetching phases from the API in milliseconds
const TIMEOUT_MS = 60000;

// Prevent merging/splitting of phases that are not at least 15 min old
export const MIN_PHASE_SPLIT_MERGE_AGE = 15;
export const PHASE_LIST_UNIT_MEASURE = {
    kg: 'kg',
    litre: 'L'
};

// Counter for generating unique product run IDs
let productRunIdCounter = 0;

/**
 * Validate phases to be merged.
 * @param {Array} phases
 * @returns {Boolean} true if the phases are valid for merge, false otherwise.
 */
export function isValidMerge(phases) {
    // check that there are at least two phases to be merged
    if (phases.length <= 1) {
        return false;
    }

    // get indices of selected rows
    const selectedIndices = phases.map(phase => phase.rowIndex);
    // order the indices
    const selectedIndicesOrdered = selectedIndices.sort((a, b) => a - b);
    // check that there is no gap in the selected rows
    for (let i = 0; i < selectedIndicesOrdered.length - 1; i++) {
        if (selectedIndicesOrdered[i + 1] - selectedIndicesOrdered[i] > 1) {
            return false;
        }
    }

    return true;
}

/**
 * Validate phases to be merged.
 * @param {Array} phases
 * @returns {Boolean} true if the phases are valid for bulk update, false otherwise.
 */
export function isValidBulkUpdate(phases) {
    // check that there is at least one phase to be updated
    if (phases.length < 1) {
        return false;
    }
    return true;
}

/**
 * Format a raw phase object to the structure expected by the frontend.
 * @param {Object} rawPhase The raw phase object from the API.
 * @returns {Object|null} The formatted phase object or null if invalid.
 */
function formatPhase(rawPhase) {
    if (!rawPhase || typeof rawPhase !== 'object') return null;

    const {
        id = null,
        uuid = null,
        asset = null,
        asset_name = '',
        phase = null,
        start = null,
        end = null,
        product = null,
        product_name = '',
        cause = null,
        cause_origin = '',
        cause_name = '',
        description = '',
        proximity_reading = null,
        duration_s = 0,
        max_downtime_delay = 0,
        max_downtime_justification = 0,
        is_major = false,
        locked_transition_to_running = false,
        product_count = null,
        product_quantity = null,
        unit_measure = null,
        production = {
            gross_production: null,
            reject_production: null,
            unit_measure: null,
            unit_packing: null
        },
        media = []
    } = rawPhase;

    return {
        id,
        uuid,
        asset,
        asset_name,
        phase,
        start,
        end,
        product,
        product_name,
        cause,
        cause_origin,
        cause_name,
        description,
        proximity_reading,
        duration_s,
        max_downtime_delay,
        max_downtime_justification,
        is_major,
        locked_transition_to_running,
        rowIndex: 0, // Will be set later
        selected: false, // Default for frontend handling
        product_count,
        product_quantity,
        unit_measure,
        production,
        media: media.map(media => media.id)
    };
}

/**
 * Processes and validates a phase object, adding a row index.
 * @param {Object} phase - The phase object to process
 * @param {string} phase.start - Start date of the phase
 * @param {string|null} phase.end - End date of the phase, can be null
 * @param {number} rowIndex - Index indicating the row position
 * @returns {(Object|null)} The processed phase object with row index or null if invalid
 */
function processPhase(phase, rowIndex) {
    if (!phase || typeof phase !== 'object') return null;

    const hasValidTimeRange =
        (new Date(phase.end) - new Date(phase.start) > 0 ||
            phase.end === null) &&
        phase.start;

    if (!hasValidTimeRange) return null;

    return {
        ...phase,
        rowIndex // Assign row index
    };
}

/**
 * Fetch phases from the API, format and normalize them, and update the table schema.
 * @param {string} url The API endpoint URL.
 * @param {Object} table The table object to update.
 * @returns {Promise<Object>} The updated table object.
 */
export async function fetchPhases(url, table) {
    if (!url) throw new Error('Invalid URL provided');

    try {
        const response = await axios.get(url, { timeout: TIMEOUT_MS });
        const { results: phases = [] } = response.data;

        // Compute initial row index based on pagination
        const startingRowIndex =
            (table.pagination.page - 1) * table.pagination.rowsPerPage;

        // Format, normalize, and filter phases
        const processedPhases = phases.reduce((acc, phase, index) => {
            const formattedPhase = formatPhase(phase);
            const processedPhase = processPhase(
                formattedPhase,
                startingRowIndex + index
            );
            if (processedPhase) {
                const processedPhaseWithProduction =
                    process_phase_with_production(processedPhase);
                acc.push(processedPhaseWithProduction);
            }
            return acc;
        }, []);

        // Group by product and set table data to summarized structure
        table.data = groupPhasesByProduct(processedPhases);

        // Update pagination metadata to reflect grouped rows
        table.pagination.rowsNumber = table.data.length;

        return table;
    } catch (error) {
        console.error('Failed to fetch phases: ', error);
        notifyError(
            i18n.global.t(
                'Failed to fetch phases at this time. Please try again later.',
                error
            )
        );

        // Return an empty table on error
        table.data = [];
        return table;
    }
}

////// Phase production functions //////
/**
 * Calculate the phase' product count (net production).
 * Null is returned if either gross_production or reject_production is null. This means that sensor data is incomplete/missing for the phase.
 * 0 is returned when there is actually no production.
 * @param {Number} gross_production
 * @param {Number} reject_production
 * @returns {Number | null} phase product count.
 */
export function calculate_phase_product_count(
    gross_production,
    reject_production
) {
    if (gross_production !== null && reject_production !== null) {
        return gross_production - reject_production;
    }
    return null;
}

/**
 * Calculate the phase' product quantity based on the product count and unit packing.
 * @param {*} product_count
 * @param {*} unit_packing
 * @returns {Number | null} phase product quantity.
 */
export function calculate_phase_product_quantity(product_count, unit_packing) {
    if (product_count !== null && unit_packing !== null) {
        return product_count * unit_packing;
    }

    return null;
}

/**
 * Modify the phase object by processing the raw production data
 * into user-facing fields which will be directly used in the table.
 * This adds two new processed fields to the phase object: product_count and product_quantity.
 * The raw production data is also removed from the phase object.
 * @param {Object} phase
 * @returns
 */
export function process_phase_with_production(phase) {
    const {
        gross_production = null,
        reject_production = null,
        unit_measure = null,
        unit_packing = null
    } = phase['production'];

    // add the calculated production data to the phase
    const product_count = calculate_phase_product_count(
        gross_production,
        reject_production
    );
    phase['product_count'] = product_count ? product_count : null;
    const product_quantity = calculate_phase_product_quantity(
        product_count,
        unit_packing
    );
    phase['product_quantity'] = product_quantity ? product_quantity : null;
    phase['unit_measure'] = PHASE_LIST_UNIT_MEASURE[unit_measure] || null;

    // remove the raw production data
    delete phase['production'];
    return phase;
}

/**
 * Groups phases into consecutive product runs and returns summarized rows with nested phases.
 *
 * A run is defined by consecutive phases sharing the same product ID (`phase.product`) **until**
 * either the product changes or a phase with `phase.phase === 'changeover'` is encountered.
 * This means a `changeover` phase marks the beginning of a new product run even if the product
 * remains the same.
 *
 * Input is assumed to be pre-sorted by start in descending order and both the group order and the
 * order of phases inside each group are preserved exactly as received from the API.
 *
 * @param {Array<Object>} phases - Array of processed phase objects.
 * @returns {Array<Object>} Array of grouped product runs with nested phases, preserving API order.
 */
export function groupPhasesByProduct(phases) {
    if (!Array.isArray(phases) || phases.length === 0) return [];

    const groups = [];
    let currentGroup = null;
    let currentProduct = null;

    for (const phase of phases) {
        const productId = phase.product ?? null;

        // Start a new run when product changes (strict equality, including null)
        if (currentGroup === null || productId !== currentProduct) {
            // Commit the previous run, if any
            if (currentGroup !== null) groups.push(currentGroup);

            // Begin a fresh run and immediately aggregate the current phase
            currentGroup = createProductGroup(phase);
            currentProduct = productId;
        }

        // Fold the phase into the active run
        updateGroupAggregates(currentGroup, phase);

        // If this phase marks a change-over with a different product, close current group but keep phase inside it
        if (phase.phase === 'changeover' && productId !== currentProduct) {
            groups.push(currentGroup);
            currentGroup = null;
            currentProduct = null;
        }
    }

    // Push the unfinished run, if any
    if (currentGroup !== null) groups.push(currentGroup);

    return groups;
}

/**
 * Create and initialize a new product group using data from the first phase of the run.
 *
 * @param {Object} phase - First phase in the new run.
 * @returns {Object} A fresh group object already seeded with the first phase values.
 */
function createProductGroup(phase) {
    const productRunId = `product-run-${++productRunIdCounter}`;

    return {
        id: productRunId, // Add stable ID for product run
        start: phase.start ?? null,
        end: phase.end ?? null,
        product: phase.product ?? null,
        product_name: phase.product_name ?? null,
        duration_s: 0,
        product_count: null,
        product_quantity: null,
        unit_measure: phase.unit_measure ?? null,
        expanded: true,
        selected: false,
        phases: []
    };
}

/**
 * Update an existing product group with a new phase.
 *
 * This mutates the provided `group` object directly for performance reasons.
 *
 * @param {Object} group - The product group being aggregated.
 * @param {Object} phase - The phase to fold into the group.
 */
function updateGroupAggregates(group, phase) {
    // Add product run ID to each phase for quick lookup
    phase.productRunId = group.id;

    // start = earliest
    if (
        group.start === null ||
        (phase.start && new Date(phase.start) < new Date(group.start))
    ) {
        group.start = phase.start;
    }

    // end = null if any phase is ongoing; otherwise latest
    if (phase.end === null) {
        group.end = null;
    } else if (group.end !== null) {
        if (new Date(phase.end) > new Date(group.end)) {
            group.end = phase.end;
        }
    }

    // aggregate numeric fields (treat null as 0)
    group.duration_s += Number(phase.duration_s || 0);

    if (phase.product_count !== null) {
        group.product_count += Number(phase.product_count);
    }

    if (phase.product_quantity !== null) {
        group.product_quantity += Number(phase.product_quantity);
    }

    // prefer first non-null descriptive fields
    if (group.product_name == null && phase.product_name != null) {
        group.product_name = phase.product_name;
    }
    if (group.unit_measure == null && phase.unit_measure != null) {
        group.unit_measure = phase.unit_measure;
    }

    // always keep the raw phase for drill-down views
    group.phases.push(phase);
}

/**
 * Get all phases flattened with their product run IDs for quick lookup.
 * @param {Array} productRuns - Array of product run groups
 * @returns {Array} Flattened array of phases with productRunId
 */
export function getFlattenedPhases(productRuns) {
    return productRuns.flatMap(run =>
        run.phases.map(phase => ({
            ...phase,
            productRunId: run.id
        }))
    );
}
